import cx from 'classnames';
import {filter, map} from 'lodash';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Timer, Tooltip, CallButtonProps} from 'components';
import {LOGO_ICON_PATH} from 'constants/images';
import {AssignmentInfo, OnlineSession, SessionStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {sessionUtils} from 'utils';

import {Participant} from '../Participant';

type Props = {
    sessions: OnlineSession[],
    getStatusOptions: (session: OnlineSession) => ({
        name: string;
        className: string;
    });
    renderAssignment: ({groupId, subgroupId}: AssignmentInfo) => React.ReactNode;
    renderActivityDataIcon: (session: OnlineSession) => React.ReactNode;
    renderPreSessionFlowBadge: (session: OnlineSession) => React.ReactNode;
    renderAnswerButton: (session: OnlineSession, buttonProps?: CallButtonProps, defaultNode?: React.ReactNode) => React.ReactNode;
    onOpenPrevClientSessionsModal: (session: OnlineSession) => void;
    openParticipantsModal: (sessionId: string) => void;
};

const T_KEYS = TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE;

export const SessionsGrid: React.FC<Props> = ({
    sessions, onOpenPrevClientSessionsModal, openParticipantsModal, renderActivityDataIcon, renderPreSessionFlowBadge,
    getStatusOptions, renderAssignment, renderAnswerButton
}: Props) => {
    const {t} = useTranslation();

    const getParticipantList = useCallback((session: OnlineSession) => {
        const participants = filter(session.participants, (p) => !p.removed);
        return sessionUtils.sortParticipantList(participants);
    }, []);

    const renderAnswerButtonInternal = useCallback((session: OnlineSession) =>
        renderAnswerButton(session, {size: 'large'}), [renderAnswerButton]);

    if (!sessions?.length) {
        return (
            <div className='tw-text-xl tw-text-center tw-text-default-dimmed tw-my-10'>
                <img src={LOGO_ICON_PATH} className='-md:tw-w-7/12 tw-mx-auto tw-mb-5' />
                {t(T_KEYS.MESSAGE.NO_SESSION)}
            </div>
        );
    }

    return (
        <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-4 2xl:tw-grid-cols-5 tw-gap-6'>
            {map(sessions, (session) => {
                const status = getStatusOptions(session);
                const assignmentName = renderAssignment(session.assignment);

                return (
                    <div
                        key={session.id}
                        className={`tw-flex tw-flex-col tw-h-56 
                                    tw-shadow-md tw-rounded-md tw-p-2 tw-text-sm tw-text-break-word`}
                    >
                        <div className='tw-flex tw-overflow-auto tw-mb-2'>
                            <div className='tw-flex-1'>
                                {map(getParticipantList(session), (participant, index) => (
                                    <div key={participant.id} className={cx('tw-w-full', {'tw-mt-1.5': index !== 0})}>
                                        <Participant
                                            participant={participant}
                                            session={session}
                                            renderActivityDataIcon={renderActivityDataIcon}
                                            renderPreSessionFlowBadge={renderPreSessionFlowBadge}
                                            onOpenPrevClientSessionsModal={onOpenPrevClientSessionsModal}
                                        />
                                    </div>
                                ))}
                            </div>
                            <Icon
                                name='more-vertical'
                                size='sm'
                                className='tw-ml-auto tw-cursor-pointer'
                                onClick={() => openParticipantsModal(session.id)}
                            />
                        </div>

                        <div className={cx('tw-mx-auto tw-mt-auto tw-mb-8 tw-text-center', {
                            'tw-mb-14': !session.assignment.groupId
                        })}
                        >
                            {renderAnswerButtonInternal(session)}
                            <div className='tw-flex-1 tw-mt-2'>
                                <div className='tw-flex tw-justify-between'>
                                    <div className={cx('tw-text-right', status.className,
                                        {
                                            'tw-text-alert tw-font-bold': session.status === SessionStatus.Calling,
                                            'tw-text-success tw-font-bold': session.status === SessionStatus.InSession,
                                        })}
                                    >
                                        <span className='tw-mr-1 tw-inline-block'>{status.name}</span>
                                        <Timer
                                            startDate={session.startDate}
                                            endDate={session.endDate}
                                            config={{optionalHours: true, minutes: true, seconds: true}}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {session.assignment.groupId && (
                            <div className='tw-flex tw-mb-2 '>
                                <Icon name='assignment' size='sm' className='tw-mr-2' />
                                <Tooltip
                                    style={{display: 'inline-block'}}
                                    className='tw-max-w-full tw-text-nowrap'
                                    message={assignmentName}
                                >
                                    {assignmentName}
                                </Tooltip>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};